import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { formatEuDate } from '../utils/dateFormat';
import './HousingLookup.css';

function starIcons(count) {
  const n = Math.min(5, Math.max(0, Number(count) || 0));
  return '★'.repeat(n) + '☆'.repeat(5 - n);
}

function formatMoney(amount) {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
}

function formatDate(ts) {
  return formatEuDate(ts);
}

function FeaturePill({ label, active }) {
  return (
    <span className={`housing-feature-pill${active ? ' active' : ''}`}>{label}</span>
  );
}

function StashPanel({ stashName, apiFetch, t }) {
  const [state, setState] = useState({ loading: false, data: null, error: null });

  const load = async () => {
    if (state.data || state.loading) return;
    setState({ loading: true, data: null, error: null });
    try {
      const res = await apiFetch(`/api/housing/stash/${encodeURIComponent(stashName)}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || t.stashFailed);
      setState({ loading: false, data: json, error: null });
    } catch (err) {
      setState({ loading: false, data: null, error: err.message });
    }
  };

  return (
    <div className="housing-stash-panel">
      {!state.data && !state.loading && !state.error && (
        <button className="housing-stash-load-btn" onClick={load}>
          {t.viewStash}
        </button>
      )}
      {state.loading && <p className="housing-stash-status">{t.stashLoading}</p>}
      {state.error && <p className="housing-stash-error">{state.error}</p>}
      {state.data && (
        <div className="housing-stash-content">
          <div className="housing-stash-header">
            <strong>{t.stashTitle}</strong>
            <span className="housing-stash-meta">{state.data.items.length} items</span>
          </div>
          {state.data.empty || state.data.items.length === 0 ? (
            <p className="housing-stash-empty">{t.stashEmpty}</p>
          ) : (
            <table className="housing-stash-table">
              <thead>
                <tr>
                  <th>{t.stashItem}</th>
                  <th>{t.stashCount}</th>
                </tr>
              </thead>
              <tbody>
                {state.data.items.map((item, i) => (
                  <tr key={`${item.name}-${i}`}>
                    <td><code>{item.name}</code></td>
                    <td className="housing-stash-count">{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function HousingCard({ house, apiFetch, t }) {
  const [stashOpen, setStashOpen] = useState(false);

  return (
    <div className="housing-card">
      <div className="housing-card-header">
        <div className="housing-card-title">
          <span className="housing-icon">H</span>
          <div>
            <h3>{house.houseName}</h3>
            {house.description && <p className="housing-description">{house.description}</p>}
          </div>
        </div>
        <div className="housing-card-badges">
          {house.isRenting ? (
            <span className="housing-badge renting">{t.renting}</span>
          ) : null}
          {house.shared ? (
            <span className="housing-badge shared">{t.shared}</span>
          ) : null}
          {house.stars > 0 && (
            <span className="housing-stars" title={`${house.stars} ${t.stars}`}>{starIcons(house.stars)}</span>
          )}
        </div>
      </div>

      <div className="housing-card-body">
        <div className="housing-info-grid">
          <div className="housing-info-item">
            <span className="housing-info-label">{t.owner}</span>
            <span className="housing-info-value">
              {house.playerName ? (
                <>
                  <strong>{house.playerName}</strong>
                  <code className="housing-citizen-id">{house.citizenid}</code>
                </>
              ) : (
                <code>{house.citizenid || '—'}</code>
              )}
            </span>
          </div>

          {house.price != null && (
            <div className="housing-info-item">
              <span className="housing-info-label">{t.price}</span>
              <span className="housing-info-value"><strong>{formatMoney(house.price)}</strong></span>
            </div>
          )}

          <div className="housing-info-item">
            <span className="housing-info-label">{t.lastPayment}</span>
            <span className="housing-info-value">{formatDate(house.last_payment)}</span>
          </div>

          {house.stashName && (
            <div className="housing-info-item">
              <span className="housing-info-label">{t.stashName}</span>
              <span className="housing-info-value"><code>{house.stashName}</code></span>
            </div>
          )}
        </div>

        {(house.pool != null || house.garden != null || house.camerasystem != null || house.canVisit != null) && (
          <div className="housing-features">
            <span className="housing-features-label">{t.features}:</span>
            <FeaturePill label={`${t.pool}`} active={!!house.pool} />
            <FeaturePill label={`${t.garden}`} active={!!house.garden} />
            <FeaturePill label={`${t.camera}`} active={!!house.camerasystem} />
            <FeaturePill label={`${t.canVisit}`} active={!!house.canVisit} />
          </div>
        )}

            {house.stashName && (
          <div className="housing-stash-section">
            <button
              className="housing-stash-toggle"
              onClick={() => setStashOpen(v => !v)}
            >
              {stashOpen ? `${t.hideStash}` : `${t.viewStash}`}
            </button>
            {stashOpen && (
                  <StashPanel stashName={house.stashName} apiFetch={apiFetch} t={t} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function HousingLookup() {
  const { apiFetch } = useAuth();
  const { t } = useI18n();

  const h = {
    title: t('app.housing.title'),
    subtitle: t('app.housing.subtitle'),
    searchType: t('app.housing.searchType'),
    byOwner: t('app.housing.byOwner'),
    byName: t('app.housing.byName'),
    searchPlaceholder: t('app.housing.searchPlaceholder'),
    search: t('app.housing.search'),
    searching: t('app.housing.searching'),
    noResults: t('app.housing.noResults'),
    failed: t('app.housing.failed'),
    houseName: t('app.housing.houseName'),
    owner: t('app.housing.owner'),
    price: t('app.housing.price'),
    stashName: t('app.housing.stashName'),
    lastPayment: t('app.housing.lastPayment'),
    renting: t('app.housing.renting'),
    shared: t('app.housing.shared'),
    features: t('app.housing.features'),
    pool: t('app.housing.pool'),
    garden: t('app.housing.garden'),
    camera: t('app.housing.camera'),
    canVisit: t('app.housing.canVisit'),
    stars: t('app.housing.stars'),
    viewStash: t('app.housing.viewStash'),
    hideStash: t('app.housing.hideStash'),
    stashLoading: t('app.housing.stashLoading'),
    stashFailed: t('app.housing.stashFailed'),
    stashEmpty: t('app.housing.stashEmpty'),
    stashTitle: t('app.housing.stashTitle'),
    stashItem: t('app.housing.stashItem'),
    stashCount: t('app.housing.stashCount'),
    description: t('app.housing.noDescription'),
  };

  const [searchType, setSearchType] = useState('identifier');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setResults([]);
    setSearched(true);

    try {
      const params = new URLSearchParams({ q: query.trim(), searchType });
      const res = await apiFetch(`/api/housing/search?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || h.failed);
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="housing-lookup">
      <div className="housing-search-hero">
        <div className="housing-search-hero-content">
          <span className="housing-hero-icon">H</span>
          <h1>{h.title}</h1>
          <p>{h.subtitle}</p>

          <div className="housing-type-toggle">
            <button
              type="button"
              className={searchType === 'identifier' ? 'active' : ''}
              onClick={() => setSearchType('identifier')}
            >
              {h.byOwner}
            </button>
            <button
              type="button"
              className={searchType === 'name' ? 'active' : ''}
              onClick={() => setSearchType('name')}
            >
              {h.byName}
            </button>
          </div>

          <form onSubmit={handleSearch} className="housing-search-form">
            <div className="housing-search-row">
              <input
                type="text"
                className="housing-search-input"
                placeholder={h.searchPlaceholder}
                value={query}
                onChange={e => setQuery(e.target.value)}
                required
              />
              <button type="submit" className="housing-search-btn" disabled={loading}>
                {loading ? h.searching : h.search}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="housing-results-area">
        {error && (
          <div className="housing-error">{error}</div>
        )}

        {searched && !loading && !error && results.length === 0 && (
          <div className="housing-empty">{h.noResults}</div>
        )}

        {results.length > 0 && (
          <div className="housing-results-header">
            <span>{results.length} {results.length === 1 ? 'property' : 'properties'} found</span>
          </div>
        )}

        <div className="housing-cards-grid">
          {results.map(house => (
            <HousingCard key={house.id} house={house} apiFetch={apiFetch} t={h} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default HousingLookup;
